import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { blogPosts } from "@/data/blogPosts";
import { ArrowLeft, Calendar, User, Share2 } from "lucide-react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import Footer from "@/components/Footer";
import { getCapsulasByPostId } from "@/utils/capsulaHelpers";
import { capsulas } from "@/data/capsulas";
import { useEffect } from "react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useSEO } from "@/hooks/useSEO";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const BlogPost = () => {
  const { slug } = useParams();
  const { toast } = useToast();
  
  const post = blogPosts.find((p) => p.slug === slug);
  const relatedCapsulas = post ? getCapsulasByPostId(post.id) : [];

  useSEO({
    title: post ? `${post.title} | Globaltis IA` : "Artículo no encontrado | Globaltis IA",
    description: post?.description || "Artículo no encontrado",
    ogImage: post?.image,
    ogType: "article",
    jsonLd: post ? {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.description,
      "image": post.image,
      "datePublished": post.date,
      "author": {
        "@type": "Person",
        "name": post.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "Globaltis IA",
        "url": "https://globaltisia.lovable.app"
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://globaltisia.lovable.app/blog/${post.slug}`
      }
    } : undefined
  });

  // Scroll al inicio cuando cambia el artículo
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "¡Link copiado! 📋",
        description: "Ahora puedes compartirlo donde quieras.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Breadcrumbs */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Inicio</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/blog">Blog</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="max-w-[200px] truncate">{post.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <Button variant="ghost" size="sm" onClick={handleShare} className="gap-2">
            <Share2 className="w-4 h-4" />
            Compartir
          </Button>
        </div>
      </header>

      {/* Artículo */}
      <article className="py-8 sm:py-10 md:py-12 px-3 sm:px-4" itemScope itemType="https://schema.org/Article">
        <div className="max-w-3xl mx-auto space-y-6 sm:space-y-8">
          {/* Cabecera del artículo */}
          <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 sm:gap-3">
                <Badge variant="secondary" className="bg-accent/10 text-accent-foreground">
                  {post.category}
                </Badge>
              </div>
              
              {relatedCapsulas.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-muted-foreground">Incluido en:</span>
                  {relatedCapsulas.map((capsula) => (
                    <Link key={capsula.id} to={`/capsulas/${capsula.id}`}>
                      <Badge 
                        variant="outline" 
                        className="bg-primary/5 text-primary hover:bg-primary/10 transition-colors cursor-pointer"
                      >
                        {capsula.title}
                      </Badge>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <h1 itemProp="headline" className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground leading-tight tracking-tight">
              {post.title}
            </h1>
            
            <p className="text-xl sm:text-2xl md:text-3xl text-muted-foreground font-light leading-relaxed">
              {post.description}
            </p>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground pt-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>
                  {new Date(post.date).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>
          </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{post.author}</span>
              </div>

          {/* Video o imagen destacada */}
          {(() => {
            const ytMatch = post.videoUrl?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
            if (ytMatch) {
              return (
                <div className="rounded-lg sm:rounded-2xl overflow-hidden aspect-video bg-black">
                  <iframe
                    src={`https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1&playsinline=1&rel=0`}
                    title={post.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full border-0"
                  />
                </div>
              );
            }
            if (post.videoUrl) {
              return (
                <div className="rounded-lg sm:rounded-2xl overflow-hidden">
                  <video
                    src={post.videoUrl}
                    poster={post.image}
                    controls
                    playsInline
                    className="w-full h-auto"
                  />
                </div>
              );
            }
            if (post.image) {
              return (
                <div className="rounded-lg sm:rounded-2xl overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-auto"
                  />
                </div>
              );
            }
            return null;
          })()}

          {/* Contenido del artículo */}
          {post.content && (
            <div className="prose prose-lg prose-slate dark:prose-invert max-w-none 
              prose-headings:font-light prose-headings:tracking-tight
              prose-h2:text-3xl prose-h2:md:text-4xl prose-h2:lg:text-5xl prose-h2:mt-12 prose-h2:mb-6
              prose-h3:text-2xl prose-h3:md:text-3xl prose-h3:mt-8 prose-h3:mb-4
              prose-h4:text-xl prose-h4:md:text-2xl prose-h4:mt-6 prose-h4:mb-3
              prose-p:text-lg prose-p:leading-relaxed prose-p:text-foreground/90
              prose-strong:text-foreground prose-strong:font-semibold
              prose-ul:text-lg prose-ul:leading-relaxed
              prose-ol:text-lg prose-ol:leading-relaxed
              prose-li:text-foreground/90
              prose-table:w-full prose-table:border-collapse prose-table:my-8 prose-table:text-base
              prose-thead:bg-muted/50 prose-thead:border-b prose-thead:border-border
              prose-th:px-4 prose-th:py-3 prose-th:text-left prose-th:font-semibold prose-th:text-foreground
              prose-td:px-4 prose-td:py-3 prose-td:border-b prose-td:border-border/50
              prose-tr:border-b prose-tr:border-border/30">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  h2: ({ node, children, ...props }) => {
                    const text = String(children);
                    if (text.startsWith('☕ Idea Globaltis:')) {
                      return (
                      <div className="not-prose my-12 border-l-4 border-primary/40 pl-6 py-4 bg-primary/5 rounded-r-lg">
                        <h2 className="text-2xl md:text-3xl font-light text-foreground m-0" {...props}>
                          {text.replace('☕ Idea Globaltis:', 'Idea Globaltis:')}
                        </h2>
                      </div>
                      );
                    }
                    return <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mt-12 mb-6" {...props}>{children}</h2>;
                  },
                  h3: ({ node, children, ...props }) => (
                    <h3 className="text-2xl md:text-3xl font-light tracking-tight mt-8 mb-4" {...props}>{children}</h3>
                  ),
                  h4: ({ node, children, ...props }) => (
                    <h4 className="text-xl md:text-2xl font-light tracking-tight mt-6 mb-3" {...props}>{children}</h4>
                  ),
                }}
              >
                {post.content}
              </ReactMarkdown>
            </div>
          )}

          {/* Idea Globaltis al final */}
          <div className="mt-4 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-6 border-2 border-primary/30">
            <div>
              <p className="text-lg font-semibold text-foreground leading-relaxed">
                <span className="font-bold">Idea Globaltis:</span> {post.insight.replace('☕ Idea Globaltis: ', '')}
              </p>
            </div>
          </div>


          {/* CTA final - Estilo Home */}
          <div className="pt-16 sm:pt-20 md:pt-24 mt-12 sm:mt-16 md:mt-20 border-t border-border/10">
            <div className="text-center space-y-8 sm:space-y-12 max-w-4xl mx-auto">
              <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase">
                ¿Necesitas ayuda?
              </p>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-foreground tracking-tight leading-tight">
                ¿Te interesa implementar esto en tu negocio?
              </h2>
              <p className="text-xl sm:text-2xl font-light text-muted-foreground leading-relaxed">
                En Globaltis te ayudamos a llevar estas ideas a la práctica.
              </p>
              <div className="pt-4">
                <Link 
                  to="/"
                  className="group inline-flex items-center gap-4 text-xl md:text-2xl font-light text-foreground hover:opacity-60 transition-opacity"
                >
                  <span>Hablemos de tu proyecto</span>
                  <span className="text-2xl md:text-3xl group-hover:translate-x-2 transition-transform">→</span>
                </Link>
              </div>
            </div>
          </div>

        </div>
      </article>

      {/* Continúa explorando - Estilo Steve Jobs */}
      <section className="py-24 md:py-32 lg:py-40 bg-gradient-to-b from-background to-secondary/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {(() => {
            // Si el post tiene cápsula asociada
            if (post.capsulaTheme) {
              const currentCapsula = capsulas.find(c => c.id === post.capsulaTheme);
              
              const capsulaArticles = blogPosts
                .filter(p => p.capsulaTheme === post.capsulaTheme && p.id !== post.id)
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
              
              // Si hay menos de 4 artículos del mismo tema, complementar con artículos de la misma categoría
              let articlesToShow = [...capsulaArticles];
              if (articlesToShow.length < 4) {
                const sameCategoryArticles = blogPosts
                  .filter(p => p.category === post.category && p.id !== post.id && p.capsulaTheme !== post.capsulaTheme)
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                articlesToShow = [...articlesToShow, ...sameCategoryArticles].slice(0, 4);
              }
              
              // Si no hay artículos, no mostrar la sección
              if (articlesToShow.length === 0) {
                return null;
              }
              
              const recentArticles = articlesToShow.slice(0, 4);
              
              return (
                <>
                  {/* Título Hero */}
                  <div className="text-center space-y-8 mb-20 md:mb-28">
                    <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase">
                      Más contenido
                    </p>
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-tight">
                      Continúa explorando
                    </h2>
                    {currentCapsula && (
                      <p className="text-xl md:text-2xl font-light text-muted-foreground leading-relaxed">
                        Descubre más sobre {currentCapsula.title}
                      </p>
                    )}
                  </div>

                  {/* Grid 2 columnas - Solo 4 artículos */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 mb-16 md:mb-24">
                    {recentArticles.map((article, index) => (
                      <Link
                        key={article.id}
                        to={`/blog/${article.slug}`}
                        className="group block"
                        style={{
                          animation: `fade-in 0.6s ease-out ${index * 0.1}s backwards`
                        }}
                      >
                        {/* Imagen 3:2 */}
                        <div className="relative overflow-hidden rounded-2xl mb-6 shadow-lg group-hover:shadow-2xl transition-shadow duration-500">
                          <AspectRatio ratio={3 / 2}>
                            <img
                              src={article.image}
                              alt={article.title}
                              className="object-cover w-full h-full group-hover:scale-[1.02] transition-transform duration-700"
                            />
                          </AspectRatio>
                        </div>

                        {/* Solo título - Grande */}
                        <h3 className="text-2xl md:text-3xl lg:text-4xl font-light leading-tight group-hover:text-primary transition-colors duration-300">
                          {article.title}
                        </h3>
                      </Link>
                    ))}
                  </div>

                  {/* CTA Minimalista - Estilo Home */}
                  {capsulaArticles.length >= 4 && currentCapsula && (
                    <div className="text-center space-y-8 mt-32 md:mt-40 pt-16 border-t border-border/10">
                      {capsulaArticles.length > 4 && (
                        <p className="text-xl md:text-2xl font-light text-muted-foreground leading-relaxed">
                          {capsulaArticles.length - 4} artículo{capsulaArticles.length - 4 > 1 ? 's' : ''} más en esta colección
                        </p>
                      )}
                      
                      <div className="pt-4">
                        <Link
                          to={`/capsulas/${post.capsulaTheme}`}
                          className="group inline-flex items-center gap-4 text-xl md:text-2xl font-light text-foreground hover:opacity-60 transition-opacity"
                        >
                          <span>{currentCapsula.title}</span>
                          <span className="text-2xl md:text-3xl group-hover:translate-x-2 transition-transform">→</span>
                        </Link>
                      </div>
                    </div>
                  )}
                </>
              );
            }
            
            // Fallback: si no tiene cápsula, mostrar artículos similares
            const otherPosts = blogPosts.filter((p) => p.id !== post.id);
            const sameCategory = otherPosts.filter(
              (p) => p.category === post.category
            ).slice(0, 4);
            
            if (sameCategory.length === 0) {
              return null;
            }
            
            return (
              <>
                <div className="text-center space-y-8 mb-20 md:mb-28">
                  <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase">
                    Artículos relacionados
                  </p>
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-tight">
                    Continúa explorando
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
                  {sameCategory.map((article, index) => (
                    <Link
                      key={article.id}
                      to={`/blog/${article.slug}`}
                      className="group block"
                      style={{
                        animation: `fade-in 0.6s ease-out ${index * 0.1}s backwards`
                      }}
                    >
                      <div className="relative overflow-hidden rounded-2xl mb-6 shadow-lg group-hover:shadow-2xl transition-shadow duration-500">
                        <AspectRatio ratio={3 / 2}>
                          <img
                            src={article.image}
                            alt={article.title}
                            className="object-cover w-full h-full group-hover:scale-[1.02] transition-transform duration-700"
                          />
                        </AspectRatio>
                      </div>

                      <h3 className="text-2xl md:text-3xl lg:text-4xl font-light leading-tight group-hover:text-primary transition-colors duration-300">
                        {article.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </>
            );
          })()}
          
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPost;
